import { Box, Checkbox, Flex, GridItem, Text } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";

import { AuthMode, FacetOption, FilterSet, useFacets } from "@nens/ldo/api-v1";

import { useScenarioStatusDescription } from "../scenario-status/scenario-status";

interface FacetProps {
  authMode: AuthMode;
  title: string;
  filter: string;
  filters: FilterSet;
  setFilters: React.Dispatch<React.SetStateAction<FilterSet>>;
}

function _compareOptions(optionsA: FacetOption[], optionsB: FacetOption[]) {
  // Return true if optionsA and optionsB are the same, otherwise false
  if (optionsA.length !== optionsB.length) return false;

  for (let i = 0; i < optionsA.length; i++) {
    if (optionsA[i].count !== optionsB[i].count || optionsA[i].option !== optionsB[i].option)
      return false;
  }

  return true;
}

export function Facet({ authMode, title, filter, filters, setFilters }: FacetProps) {
  const getStatusText = useScenarioStatusDescription();

  // To prevent flickering while new facets are loading, we keep the previous
  // value in state until new one arrives.
  const [currentFacets, setCurrentFacets] = useState<FacetOption[]>([]);

  const options = useFacets(authMode, filter, filters);

  useEffect(() => {
    if (options.isSuccess && !_compareOptions(currentFacets, options.data)) {
      setCurrentFacets(options.data);
    }
  }, [options.isSuccess, options.data, currentFacets]);

  const checkedValues = filters.checkedValues(filter);

  const showFacet = currentFacets.length > 1 || checkedValues.length > 0;

  const checkedButNotInFacet = checkedValues.filter(
    (value) => !currentFacets.find((facet) => facet.option === value)
  );

  if (!showFacet) return null;

  return (
    <GridItem px="8px" py="2px" borderBottom="1px" fontWeight="semibold" borderColor={"gray.200"}>
      <Text fontSize="sm" py="6px" fontWeight="semibold">
        {title}
      </Text>
      <Box pb="8px" maxH="200px" overflowY="scroll">
        {checkedButNotInFacet.map((option) => (
          <Flex
            key={`checked-${option}`}
            justifyContent="space-between"
            alignItems="center"
            fontWeight="normal"
          >
            <Checkbox
              size="md"
              onChange={(e) => {
                setFilters((filterSet) => filterSet.check(filter, option, e.target.checked));
              }}
              isChecked
            >
              {filter === "status" ? getStatusText("" + option) : option}
            </Checkbox>
          </Flex>
        ))}
        {currentFacets.map((option) => {
          const checked = filters.isChecked(filter, option.option);
          return (
            <Flex
              key={option.option}
              justifyContent="space-between"
              alignItems="center"
              fontWeight="normal"
            >
              <Checkbox
                size="md"
                onChange={(e) => {
                  setFilters((filterSet) =>
                    filterSet.check(filter, option.option, e.target.checked)
                  );
                }}
                isChecked={checked}
              >
                {filter === "status" ? getStatusText("" + option.option) : option.option}
                {!checked ? ` (${option.count})` : ""}
              </Checkbox>
            </Flex>
          );
        })}
      </Box>
    </GridItem>
  );
}
